import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Save } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import type { Equipo } from "@/lib/equipos";
import {
  type DatosEtiqueta,
  etiquetaProcedimiento,
  etiquetaProcedimientoNoAsignable,
  type Procedimiento,
} from "@/lib/procedimientos";

// Espejo de la validación de servidor (loto/dto.go): equipo obligatorio; número de LOTO
// opcional ≤30 (vacío ⇒ el servidor autogenera LOTO-YYYY-XXXX); descripción ≤500,
// observaciones ≤1000. El id del equipo se coacciona a número (el <select> entrega string).
// El procedimiento (#236) es OPCIONAL: viaja como cadena (lo que entrega el <select>) y
// vacío ⇒ el LOTO se abre sin procedimiento.
const lotoSchema = z.object({
  id_equipo: z.coerce.number().int().positive("Selecciona un equipo"),
  id_procedimiento: z.string(),
  numero_loto: z.string().trim().max(30, "Máximo 30 caracteres"),
  descripcion: z.string().trim().max(500, "Máximo 500 caracteres"),
  observaciones: z.string().trim().max(1000, "Máximo 1000 caracteres"),
});

// El esquema coacciona id_equipo (z.coerce.number): el tipo de entrada difiere del de
// salida ya validado. Con @hookform/resolvers v5 hay que tipar useForm con ambos.
type LotoFormInput = z.input<typeof lotoSchema>;
export type LotoFormValues = z.output<typeof lotoSchema>;

/**
 * Elegido es el procedimiento que el técnico tiene seleccionado, con lo que se sepa para
 * nombrarlo aunque el catálogo ya no lo traiga (#293 D3/E5). `datos` es null mientras el
 * catálogo no lo haya traído NUNCA: solo entonces se nombra por su id (#303 D9), porque un
 * código que se llegó a conocer se CONSERVA aunque el procedimiento desaparezca (#303 D8).
 */
type Elegido = { id: number; datos: DatosEtiqueta | null };

// recordarPorId construye el `Elegido` cuando lo único conocido es el id del formulario.
// Devuelve null si no hay id (o no es un id válido): entonces no hay nada que recordar ni que
// enseñar.
function recordarPorId(idProcedimiento: string): Elegido | null {
  const id = Number(idProcedimiento);
  if (idProcedimiento === "" || !Number.isInteger(id) || id <= 0) {
    return null;
  }
  return { id, datos: null };
}

// mismosDatos compara solo lo que la etiqueta usa: un refetch que trae el mismo código y la
// misma versión no tiene por qué mover nada (#293).
function mismosDatos(a: DatosEtiqueta | null, b: DatosEtiqueta | null): boolean {
  if (a == null || b == null) {
    return a === b;
  }
  return (
    a.codigo_procedimiento === b.codigo_procedimiento &&
    a.version_procedimiento === b.version_procedimiento
  );
}

// Mensaje del bloqueo del alta (#293 E1). Es LITERALMENTE el que ya usa el hermano
// `CambiarProcedimientoDialog`: las dos pantallas dicen lo mismo ante el mismo problema.
const PROCEDIMIENTO_NO_DISPONIBLE =
  "Este procedimiento ya no está disponible. Selecciona uno de la lista.";

function aplicarError(
  err: unknown,
  setError: UseFormSetError<LotoFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "NUMERO_DUPLICADO") {
      setError("numero_loto", { message: "Ya existe un LOTO con ese número." });
      return;
    }
    if (err.code === "EQUIPO_NO_LOTEABLE") {
      setError("id_equipo", { message: "Este equipo no admite bloqueo LOTO." });
      return;
    }
    if (err.code === "EQUIPO_INACTIVO") {
      setError("id_equipo", { message: "Este equipo está inactivo." });
      return;
    }
    if (err.code === "VALIDACION" && err.fields) {
      let mapped = false;
      for (const campo of ["id_equipo", "numero_loto", "descripcion", "observaciones"] as const) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    setFormError(err.message || "No se pudo abrir el LOTO.");
    return;
  }
  setFormError("No se pudo abrir el LOTO. Comprueba tu conexión.");
}

/**
 * LotoForm es el formulario de apertura de un LOTO (react-hook-form + zod). El equipo se
 * elige de un desplegable limitado a los loteables y activos (los únicos sobre los que se
 * puede abrir un LOTO). Bajo él, el PROCEDIMIENTO de aislamiento (#236): opcional, limitado a
 * los procedimientos ACTIVOS del equipo elegido y preseleccionado, AL ELEGIR EQUIPO, cuando
 * ese equipo tiene uno solo (#293 D2/E3). El catálogo no tiene ninguna otra vía para escribir
 * en el formulario: lo que el técnico eligió se conserva aunque deje de ser asignable y sigue
 * a la vista MARCADO —«(retirado)» si lo desactivaron, «(no disponible)» por cualquier otro
 * motivo; el vocabulario único de #303— (#293 D1/D3), pero entonces el alta **no sale**
 * hasta que elija otro (#293 E1: el servidor rechaza el procedimiento borrado y acepta el
 * desactivado; su validación de `activo` llega con #302). El número es opcional: si se deja
 * vacío lo autogenera el servidor. Mapea los errores de negocio del backend a errores por
 * campo (número duplicado, equipo no loteable/inactivo), replicando el patrón de los maestros
 * de fábrica.
 */
export function LotoForm({
  defaultValues,
  equipos,
  procedimientos,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: LotoFormValues;
  equipos: Equipo[];
  // procedimientos es el catálogo COMPLETO de la obra (#236); el formulario se queda con los
  // activos del equipo elegido.
  procedimientos: Procedimiento[];
  pending: boolean;
  onSubmit: (values: LotoFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LotoFormInput, unknown, LotoFormValues>({
    resolver: zodResolver(lotoSchema),
    defaultValues,
  });

  // Procedimientos ofrecidos: solo los ACTIVOS del equipo elegido (#236 R1). Los de otros
  // equipos y los dados de baja no aparecen; el servidor revalida la pertenencia al equipo al
  // aplicar (loto/service.go → 422), así que esto es comodidad, no una garantía.
  const idEquipo = Number(watch("id_equipo"));
  const elegido = watch("id_procedimiento");
  const delEquipo = useMemo(
    () => procedimientos.filter((p) => p.activo && p.id_equipo === idEquipo),
    [procedimientos, idEquipo],
  );

  // La selección es del técnico: solo la cambian él o un cambio de EQUIPO (#293 D4). El
  // efecto se gobierna por el id del equipo guardado en un ref —no por la identidad de la
  // lista—, así que un refetch del catálogo o una invalidación desde el mantenimiento de
  // procedimientos no reescriben el formulario, y el montaje respeta el valor con el que
  // nace (D5). Al cambiar de equipo la selección anterior se DESCARTA (R3 de #236: nunca se
  // envía el procedimiento de otro equipo) y, si el equipo nuevo tiene exactamente uno
  // activo, se preselecciona (R2 de #236); con cero o con varios, arranca vacía.
  const equipoPrevio = useRef(defaultValues.id_equipo);
  useEffect(() => {
    if (equipoPrevio.current === idEquipo) {
      return;
    }
    equipoPrevio.current = idEquipo;
    setValue("id_procedimiento", delEquipo.length === 1 ? String(delEquipo[0].id) : "");
  }, [idEquipo, delEquipo, setValue]);

  // Excepción acotada a lo anterior (#293 E3): si el técnico eligió equipo con el catálogo
  // aún EN VUELO, aquel efecto consumió el cambio con la lista vacía y la preselección de R2
  // (#236) no llegó a ocurrir nunca. La recupera la primera respuesta CON CONTENIDO —no la
  // primera llegada: un `[]` legítimo, una obra que aún no tiene procedimientos, no gasta el
  // turno y lo recoge el refetch que traiga el primero—, y solo esa: exige que el campo siga
  // vacío —no pisa ninguna selección— y no vuelve a actuar, que es lo que protege D2.
  const catalogoLlegado = useRef(procedimientos.length > 0);
  useEffect(() => {
    if (catalogoLlegado.current || procedimientos.length === 0) {
      return;
    }
    catalogoLlegado.current = true;
    if (elegido === "" && delEquipo.length === 1) {
      setValue("id_procedimiento", String(delEquipo[0].id));
    }
  }, [procedimientos, delEquipo, elegido, setValue]);

  // El procedimiento elegido se RECUERDA: nace del valor de partida (E5, sin más que su id) y
  // se completa con su código y versión en cuanto se ve en el catálogo. Si otro usuario lo da
  // de baja o lo borra con el formulario abierto, la selección se conserva (D1) y el
  // desplegable puede seguir mostrándola, en vez de dejar al técnico ante un selector vacío
  // enviando un id invisible (D3). La memoria es ACUMULATIVA (#303 D8): que el catálogo deje
  // de traerlo no borra el código que ya se conocía, solo cambia la marca.
  const [recordado, setRecordado] = useState<Elegido | null>(() =>
    recordarPorId(defaultValues.id_procedimiento),
  );
  useEffect(() => {
    const base = recordarPorId(elegido);
    const enCatalogo = procedimientos.find((p) => String(p.id) === elegido);
    setRecordado((previo) => {
      if (base == null) {
        return null;
      }
      // Mientras el catálogo lo traiga, manda él; cuando deja de traerlo, se conserva lo ya
      // sabido de ESE mismo procedimiento (otro id no hereda nada).
      const datos = enCatalogo ?? (previo?.id === base.id ? previo.datos : null);
      // Sin cambio real no se re-renderiza: un refetch no tiene por qué mover nada (#293).
      return previo?.id === base.id && mismosDatos(previo.datos, datos)
        ? previo
        : {
            id: base.id,
            datos:
              datos == null
                ? null
                : {
                    codigo_procedimiento: datos.codigo_procedimiento,
                    version_procedimiento: datos.version_procedimiento,
                  },
          };
    });
  }, [procedimientos, elegido]);

  // disponible dice si lo elegido está entre los ACTIVOS del equipo, que es lo único que el
  // alta puede enviar (E1/R7). Vacío es válido: el procedimiento es opcional (#236).
  const disponible = useCallback(
    (id: string) => id === "" || delEquipo.some((p) => String(p.id) === id),
    [delEquipo],
  );

  // Opciones del desplegable: los ASIGNABLES —los activos del equipo, sin marca ninguna— y,
  // cuando lo elegido ya no está entre ellos, el propio elegido con la marca que le
  // corresponde —una sola vez: mientras siga asignable no se duplica—. Sigue a la vista aunque
  // el alta esté bloqueada (D3): el técnico tiene que poder ver qué había puesto para decidir
  // con qué lo sustituye, y verlo MARCADO, porque el envío lo va a rechazar (#303 E3/D10).
  const opciones = useMemo(() => {
    const asignables = delEquipo.map((p) => ({ id: p.id, etiqueta: etiquetaProcedimiento(p) }));
    if (disponible(elegido) || recordado == null || String(recordado.id) !== elegido) {
      return asignables;
    }
    // La precedencia de #303 E3/D10 —la marca sale de la ASIGNABILIDAD, nunca de `activo` a
    // secas— vive UNA sola vez, en `lib/` (#309 D3/R5), y la comparte con el diálogo de cambio
    // de procedimiento. Lo que aquí se conoce del procedimiento cuando el catálogo ya no lo
    // trae es lo que el formulario RECORDABA (#293 D8).
    const enCatalogo = procedimientos.find((p) => String(p.id) === elegido);
    return [
      ...asignables,
      {
        id: recordado.id,
        etiqueta: etiquetaProcedimientoNoAsignable(recordado.id, enCatalogo, recordado.datos),
      },
    ];
  }, [delEquipo, disponible, elegido, procedimientos, recordado]);

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    // El alta NO sale con un procedimiento que ya no está entre los activos del equipo
    // (#293 E1/R7). La selección se conserva y se ve (D1/D3), pero aquí se para: el servidor
    // rechaza el procedimiento BORRADO y acepta el DESACTIVADO —la validación de `activo` al
    // abrir el LOTO es la issue #302—, así que sin este freno el LOTO nacería con un
    // procedimiento retirado sin que nadie dijera nada.
    if (!disponible(values.id_procedimiento)) {
      setError("id_procedimiento", { message: PROCEDIMIENTO_NO_DISPONIBLE });
      return;
    }
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_equipo">Equipo</Label>
        <Select
          id="id_equipo"
          aria-invalid={errors.id_equipo ? true : undefined}
          {...register("id_equipo")}
        >
          <option value="0" disabled>
            Selecciona un equipo
          </option>
          {equipos.map((equipo) => (
            <option key={equipo.id} value={equipo.id}>
              {equipo.codigo_equipo} · {equipo.nombre_equipo}
            </option>
          ))}
        </Select>
        <FieldError>{errors.id_equipo?.message}</FieldError>
      </div>

      {/* Procedimiento de aislamiento (#236): opcional, del equipo elegido y solo los vivos. */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_procedimiento">Procedimiento (opcional)</Label>
        <Select
          id="id_procedimiento"
          aria-invalid={errors.id_procedimiento ? true : undefined}
          {...register("id_procedimiento")}
        >
          <option value="">Sin procedimiento</option>
          {opciones.map((opcion) => (
            <option key={opcion.id} value={opcion.id}>
              {opcion.etiqueta}
            </option>
          ))}
        </Select>
        <p className="text-slate-500 text-sm">
          Los pasos de aislamiento quedarán a mano en la ficha del LOTO.
        </p>
        <FieldError>{errors.id_procedimiento?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="numero_loto">Número de LOTO (opcional)</Label>
        <Input
          id="numero_loto"
          autoComplete="off"
          aria-invalid={errors.numero_loto ? true : undefined}
          {...register("numero_loto")}
        />
        <p className="text-slate-500 text-sm">Se genera automáticamente si se deja vacío.</p>
        <FieldError>{errors.numero_loto?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descripcion">Descripción (opcional)</Label>
        <Textarea
          id="descripcion"
          aria-invalid={errors.descripcion ? true : undefined}
          {...register("descripcion")}
        />
        <FieldError>{errors.descripcion?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="observaciones">Observaciones (opcional)</Label>
        <Textarea
          id="observaciones"
          aria-invalid={errors.observaciones ? true : undefined}
          {...register("observaciones")}
        />
        <FieldError>{errors.observaciones?.message}</FieldError>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          Abrir LOTO
        </Button>
      </div>
    </form>
  );
}
